import { createElement } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { YouTubePlayer } from '../player/YouTubePlayer'
import type { PlayerApi } from '../player/hooks/usePlayer'
import type { PlayerConfig, PlayerEventName } from '../player/types'
import './embed.css'

export type {
  PlayerConfig,
  VideoSourceConfig,
  AdBreak,
  AdCreative,
  QualityLevel,
  PlaybackMode,
  VideoStats,
  Chapter,
  EndScreenConfig,
  AdsRemoteConfig,
} from '../player/types'

export interface PlayerInstance {
  destroy: () => void
  update: (config: PlayerConfig) => void
  el: HTMLElement
  seek: (time: number) => void
  play: () => void
  pause: () => void
  getState: () => PlayerApi['ui'] | null
  on: (event: PlayerEventName | string, fn: (payload?: unknown) => void) => () => void
  off: (event: PlayerEventName | string, fn: (payload?: unknown) => void) => void
}

export interface CreateOptions extends PlayerConfig {
  className?: string
}

function resolveTarget(target: string | HTMLElement): HTMLElement {
  if (typeof target === 'string') {
    const el = document.querySelector(target)
    if (!el) throw new Error(`PlayerV: no element matches "${target}"`)
    return el as HTMLElement
  }
  return target
}

function normalizeConfig(config: CreateOptions): PlayerConfig {
  return {
    content: config.content,
    adBreaks: config.adBreaks ?? [],
    ads: config.ads,
    stats: config.stats,
    chapters: config.chapters,
    thumbnails: config.thumbnails,
    endScreen: config.endScreen,
    startTime: config.startTime,
    resume: config.resume ?? { enabled: true },
    audioTracks: config.audioTracks,
    live: config.live,
    ambient: config.ambient,
    events: config.events,
    autoplay: config.autoplay,
    muted: config.muted,
    theater: config.theater,
    miniPlayer: config.miniPlayer,
    share: config.share,
    sleepTimer: config.sleepTimer,
    offline: config.offline,
    cast: config.cast,
  }
}

/** Mount PlayerV into any page element. */
export function create(target: string | HTMLElement, config: CreateOptions): PlayerInstance {
  const host = resolveTarget(target)
  const wrapper = document.createElement('div')
  wrapper.className = ['playerv-embed', config.className].filter(Boolean).join(' ')
  wrapper.style.width = '100%'
  host.innerHTML = ''
  host.appendChild(wrapper)

  let currentConfig = normalizeConfig(config)
  let root: Root | null = createRoot(wrapper)
  let api: PlayerApi | null = null

  const render = (cfg: PlayerConfig) => {
    root?.render(
      createElement(YouTubePlayer, {
        config: cfg,
        onApi: (next) => {
          api = next
        },
      }),
    )
  }

  render(currentConfig)

  return {
    el: host,
    destroy() {
      root?.unmount()
      root = null
      api = null
      host.innerHTML = ''
    },
    update(next: PlayerConfig) {
      currentConfig = normalizeConfig(next)
      render(currentConfig)
    },
    seek(time: number) {
      api?.seek(time)
    },
    play() {
      void api?.requestPlay()
    },
    pause() {
      api?.pause()
    },
    getState() {
      return api?.ui ?? null
    },
    on(event, fn) {
      if (!api) {
        // attach after ready via host CustomEvent
        const handler = (e: Event) => fn((e as CustomEvent).detail)
        host.addEventListener(`playerv:${event}`, handler)
        return () => host.removeEventListener(`playerv:${event}`, handler)
      }
      return api.on(event, fn)
    },
    off(event, fn) {
      api?.off(event, fn)
    },
  }
}

/** Auto-init elements with `data-playerv` + JSON in `data-config`. */
export function autoInit(selector = '[data-playerv]'): PlayerInstance[] {
  const nodes = Array.from(document.querySelectorAll(selector))
  return nodes.map((node) => {
    const raw = node.getAttribute('data-config')
    if (!raw) throw new Error('PlayerV: data-config is required on [data-playerv]')
    const parsed = JSON.parse(raw) as CreateOptions
    return create(node as HTMLElement, parsed)
  })
}

export const PlayerV = { create, autoInit }

declare global {
  interface Window {
    PlayerV: typeof PlayerV
  }
}

if (typeof window !== 'undefined') {
  window.PlayerV = PlayerV
}
